import { useState, useEffect } from "react";
import { createComponent } from "react-fela";
import { combineRules } from "fela";
import { colors, gutters, layout as sizing } from "~/styleguide";
import { textStyles, themeText, themeHeadline } from "~/styleguide/typography";
import { fluid, aspectMinHeight } from "~/styleguide/utils";
import { featureFiligree } from "~/styleguide/filigree";
import { makeAutoplaySource, stripSecurityAttributes } from "~/modules/video";

// Context
import { getThemeForModule } from "~/context/theme";

// Components
import Visual from "~/components/shared/visual";
import HubSpotForm from "~/components/shared/hubspot/form";
import { ContentMaxWidth } from "~/components/layout/max-width";
import Modal from "~/components/shared/modal";
import useModal from "~/components/shared/modal/useModal";
import AspectContainer from "~/components/shared/aspect-container";
import DeferredEmbed from "~/components/shared/deferred-embed";
import People from "./people";
import MarqueeCTA from "~/components/shared/marquee/cta";
import AnchorTarget from "~/components/shared/anchor-target";

export default function MarqueeFeature({ block, isLcpCandidate }) {
    // Pull ACF fields
    const {
        anchor_id,
        align_copy_to_top,
        caption,
        heading,
        non_marquee,
        heading_size = "regular",
        copy,
        heading_html,
        layout = "left",
        link,
        link_label,
        link_content,
        link_type,
        secondary_link = "none", // none, video, link
        secondary_link_type, // url, content
        secondary_link_url,
        secondary_link_content,
        image,
        image_column_layout,
        second_image,
        theme_colors,
        hide_filigree = false,
        hubspot_form_id,
        go_to_webinar_key,
        type,
        video = "",
        video_embed,
        video_button_label,
        related_people,
        form_cta_label,
        video_player,
        preload,
        reduce_vpadding,
    } = block;

    const { isShowing, toggle } = useModal();

    // Determine color theme
    const theme = getThemeForModule(theme_colors);
    const textTheme = themeText(theme);
    const headlineTheme = themeHeadline(theme);
    const shouldPreloadVisual =
        isLcpCandidate === true || (isLcpCandidate === undefined && preload);

    // Is showing form? For use in library layout only
    const [isShowingForm, setIsShowingForm] = useState(false);

    /**
     * Listen to hash changes that match this block and change to show the form
     */
    useEffect(() => {
        window.addEventListener("hashchange", onHashChange, false);
        return function cleanUp() {
            window.removeEventListener("hashchange", onHashChange, false);
        };
    });

    const onHashChange = function() {
        const hash = window.location.hash.substring(1);
        if (
            hash &&
            hash.length > 1 &&
            hash === anchor_id &&
            type === "library"
        ) {
            setIsShowingForm(true);
        }
    };

    const MarqueeForm = () => {
        // Form ID is required
        if (!hubspot_form_id) return null;

        // Only show this type of form under specific conditions
//        if (type === "webinar"  || (type === "library" && isShowingForm))
            return (
                <HubSpotForm
                    formId={hubspot_form_id}
                    goToWebinarKey={go_to_webinar_key}
                    prefix="feature-marquee"
                    extend={formStyles}
                />
            );
//        else return null;
    };

    return (
        <Container
            extend={{
                background: (theme.gradient ? theme.gradient : theme.primary)
            }}
            className="module-marquee-feature"
            reducePadding={reduce_vpadding}
        >
            {/* Anchor with a bit of offset to account for main header */}
            {anchor_id && <AnchorTarget id={anchor_id} />}

            {/* Main filigree for Left-aligned marquees */}
            {!hide_filigree && layout && layout === "left" && (
                <FiligreeBackground
                    extend={{
                        backgroundImage: `url("${featureFiligree(
                            theme.secondaryEscaped
                        )}")`
                    }}
                />
            )}

            {!hide_filigree && layout && layout === "centered" && (
                <>
                    <FiligreeBackground
                        side="left"
                        extend={{
                            backgroundImage: `url("${featureFiligree(
                                theme.secondaryEscaped
                            )}")`
                        }}
                    />
                    <FiligreeBackground
                        side="right"
                        extend={{
                            backgroundImage: `url("${featureFiligree(
                                theme.secondaryEscaped
                            )}")`
                        }}
                    />
                </>
            )}

            {["right_aligned", "fill"].includes(image_column_layout) && !video && (
                <Visual
                    isLcpCandidate={isLcpCandidate}
                    load={shouldPreloadVisual}
                    image={image}
                    background={
                        image_column_layout === "right_aligned"
                            ? "contain"
                            : "cover"
                    }
                    extend={combineRules(
                        rightAlignedImageStyles(image_column_layout)
                    )}
                />
            )}

            <ContentMaxWidth
                extend={{ maxWidth: sizing.marqueeContentMaxWidth }}
            >
                <Wrapper extend={contentWrapperStyles(layout)}>
                    {/* MEDIA COLUMN */}
                    {layout !== "centered" && (
                        <ImageContainer
                            type={type}
                            layout={image_column_layout}
                            noMaxHeight={isShowingForm || type === "webinar"}
                            video_player={video_player}
                        >
                            {image && video && (
                                <Visual
                                    isLcpCandidate={isLcpCandidate}
                                    load={shouldPreloadVisual}
                                    autoplay
                                    playsinline
                                    muted
                                    loop
                                    deferVideoOnMobile
                                    video={video}
                                    image={image}
                                    extend={combineRules(
                                        imageStyles(image_column_layout),
                                        videoStyles
                                    )}
                                />
                            )}
                            {!image && video_player && (
                                <VideoContent
                                    extend={videoPlayerStyles}
                                >
                                    <DeferredEmbed
                                        className="video-player"
                                        immediate={isLcpCandidate}
                                        html={stripSecurityAttributes(
                                            makeAutoplaySource(video_player)
                                        )}
                                    />
                                </VideoContent>
                            )}
                            {image_column_layout === "centered" && image && !video && type !== "webinar" && !isShowingForm && (
                                <img src={image.url} title={image.title} alt={image.alt} style={{objectFit: 'contain'}}/>
                            )}

                            {image_column_layout !== "centered" && image && !video && type !== "webinar" && !isShowingForm && (
                              <Visual
                                  isLcpCandidate={isLcpCandidate}
                                  load={shouldPreloadVisual}
                                  image={image}
                                  align={
                                      image_column_layout === "double" ||
                                      image_column_layout === "right_aligned"
                                          ? "right"
                                          : "right"
                                  }
                                  extend={combineRules(
                                      imageStyles(image_column_layout)
                                  )}
                              />
                            )}

                            {image_column_layout === "double" &&
                                second_image && (
                                    <Visual
                                        isLcpCandidate={false}
                                        load={isLcpCandidate === undefined && preload}
                                        image={second_image}
                                        align="left"
                                        extend={combineRules(
                                            imageStyles(image_column_layout)
                                        )}
                                    />
                                )}
                            {(type === "webinar" || (type === "library" && isShowingForm)) && (
                                <MarqueeForm />
                            )}
                        </ImageContainer>
                    )}

                    {/* CONTENT COLUMN */}
                    <Content
                        type={type}
                        layout={layout}
                        verticalAlign={align_copy_to_top}
                        className="module-marquee-feature-content"
                    >
                        {/* Caption */}
                        {caption && (
                            <MarqueeEyebrow
                                extend={combineRules(
                                    textTheme,
                                    eyebrowStyles
                                )}
                                dangerouslySetInnerHTML={{
                                    __html: caption
                                }}
                            ></MarqueeEyebrow>
                        )}
                        {/* Heading */}
                        {heading && (
                            <BaseMarqueeHeading
                                as={non_marquee === true ? "h3" : "h1"}
                                headingSize={heading_size}
                                extend={combineRules(
                                    headlineTheme,
                                    marqueeHeadingStyles
                                )}
                                dangerouslySetInnerHTML={{
                                    __html: heading
                                }}
                            />
                        )}
                        {/* Copy */}
                        {copy && heading_html === false &&(
                            <MarqueeCopy
                                extend={combineRules(
                                    textTheme,
                                    marqueeCopyStyles(layout)
                                )}
                                dangerouslySetInnerHTML={{
                                    __html: copy
                                }}
                            ></MarqueeCopy>
                        )}
                        {copy && heading_html === true &&(
                            <MarqueeContainer
                                extend={combineRules(
                                    textTheme,
                                    marqueeCopyStyles(layout)
                                )}
                                dangerouslySetInnerHTML={{
                                    __html: copy
                                }}
                            ></MarqueeContainer>
                        )}
                        {related_people && (
                            <People
                                people={related_people}
                                extend={{ ...textTheme }}
                            />
                        )}
                        {type === "ebook" ? (
                            <MarqueeForm />
                        ) : (
                            <MarqueeCTA
                                type={type}
                                link={link}
                                link_label={link_label}
                                link_content={link_content}
                                link_type={link_type}
                                video_embed={video_embed}
                                theme={theme}
                                form_cta_label={form_cta_label}
                                toggle={toggle}
                                textTheme={textTheme}
                                hubspot_form_id={hubspot_form_id}
                                isShowingForm={isShowingForm}
                                setIsShowingForm={setIsShowingForm}
                                secondary_link={secondary_link}
                                secondary_link_type={secondary_link_type}
                                secondary_link_url={secondary_link_url}
                                secondary_link_content={secondary_link_content}
                                video_button_label={video_button_label}
                            />
                        )}
                    </Content>
                </Wrapper>
            </ContentMaxWidth>

            {type === "page" &&
                secondary_link === "video" &&
                video_embed &&
                video_button_label && (
                    <Modal isShowing={isShowing} hide={toggle} centered={true}>
                        <AspectContainer
                            html={stripSecurityAttributes(
                                makeAutoplaySource(video_embed)
                            )}
                        ></AspectContainer>
                    </Modal>
                )}
        </Container>
    );
}

const Container = createComponent(
    ({ reducePadding }) => ({
        overflow: "hidden",
        position: "relative",
        backgroundPosition: "97% center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "auto 100%",

        "& h2": textStyles.heading1,

        "> div": {
          marginTop: 14
        },

        extend: [
            fluid("paddingH", gutters.default),
            {
              condition: reducePadding === false,
              style: fluid("paddingV", gutters.verticalLarge),
            }
        ]
    }),
    "div",
    ["id"]
);

const FiligreeBackground = createComponent(({ side }) => ({
    backgroundPosition: "105% 60%",
    backgroundRepeat: "repeat-y",
    backgroundSize: "auto 130%",
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    left: 0,
    width: "100%",
    pointerEvents: "none",

    extend: [
        {
            condition: !side,
            style: {
                maxWidthTablet: {
                    display: "none"
                }
            }
        },
        {
            condition: !!side,
            style: {
                backgroundSize: "auto 118%",
                backgroundRepeat: "repeat-y",
                width: "26%",
                maxWidthTablet: {
                    width: "18%",
                    backgroundSize: "auto 38%"
                }
            }
        },
        {
            condition: side === "left",
            style: {
                backgroundPosition: "102% center"
            }
        },
        {
            condition: side === "right",
            style: {
                backgroundPosition: "-2% center",
                right: 0,
                left: "auto"
            }
        }
    ]
}));

const contentWrapperStyles = layout => ({
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row-reverse",

    minWidthTablet: {
        ...aspectMinHeight.parent(sizing.contentMaxWidth / 350)
    },

    maxWidthTablet: {
        flexWrap: "wrap",
        flexDirection: "column-reverse",
        textAlign: "center"
    },

    extend: [
        {
            condition: layout === "centered",
            style: {
                textAlign: "center"
            }
        }
    ]
});

const Wrapper = createComponent(() => ({}));

const Content = createComponent(({ type, layout, verticalAlign }) => ({
    // minWidthTablet: {
    ...aspectMinHeight.child,
    // },
    width: "calc(52% - 20px)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingRight: "40px",
    maxWidthTablet: {
        width: "100%",
        paddingRight: "0px"
    },
    extend: [
        {
            condition: type === "webinar" || type === "library",
            style: {
                width: "calc(50% - 40px)"
            }
        },
        {
            condition: type === "double",
            style: {
                minWidthTablet: {
                    marginRight: 30
                }
            }
        },
        {
            condition: layout === "centered",
            style: {
                width: "100%",
                maxWidth: 1000,
                marginH: "auto"
            }
        },
        {
            condition: verticalAlign && type === "webinar",
            style: {
                justifyContent: "flex-start",
                extend: [fluid("padding-top", { max: 100, min: 1 })]
            }
        }
    ]
}));

const VideoContent = createComponent(() => ({
    width: "calc(48% - 20px)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    maxWidthTablet: { width: "100%" }
}));

const ImageContainer = createComponent(({ type, noMaxHeight, layout, video_player }) => ({
    display: "flex",
    width: "calc(50% - 100px)",
    maxWidthTablet: {
        width: "100%",
        flex: "none",
        marginTop: 30
    },
    extend: [
        {
            condition: layout === "double",
            style: {
                width: "auto",
                flex: 1,
                minWidthTablet: {
                    marginRight: -180,
                    marginLeft: 40
                }
            }
        },
        {
            condition: layout === "fill",
            style: {
                maxWidthTablet: {
                    width: "calc(100% + 50px)",
                    marginTop: -40
                }
            }
        },
        {
            condition: type === "webinar" || type === "library",
            style: {
                width: "calc(50% - 40px)"
            }
        },
        {
            condition: video_player,
            style: {
                width: "50%"
            }
        },
        {
            condition: noMaxHeight,
            style: {
                maxHeight: "initial",
                height: "auto",
                alignItems: "center"
            }
        }
    ]
}));

const imageStyles = layout => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    ":nth-child(2)": {
        marginLeft: 20
    },
    "> *": {
        width: "100%"
    },
    "& .vv-visual *": {
        maxHeight: 570
    },
    extend: [
        {
            condition: ["right_aligned", "fill"].includes(layout),
            style: {
                visibility: "hidden",
                justifyContent: "fled-end",
                maxWidthTablet: {
                    visibility: "inherit"
                }
            }
        },
        {
            condition: layout === "fill",
            style: {
                left: 0,
                right: -50,
                bottom: -60,
                position: "relative"
            }
        }
    ]
});

const rightAlignedImageStyles = layout => ({
    right: 0,
    top: 0,
    bottom: 0,
    margin: "auto",
    width: "50%",
    height: "80%",
    position: "absolute",
    "& .vv-visual": {
        height: "100%"
    },
    "& .vv-asset": {
        backgroundPosition: "center right!important"
    },
    maxWidthTablet: {
        visibility: "hidden"
    },

    extend: [
        {
            condition: layout === "fill",
            style: {
                width: "62%",
                height: "100%",
                "& .vv-asset": {
                    backgroundPosition: "left center !important"
                }
            }
        }
    ]
});

const videoStyles = {
    visibility: "visible",
    "& .vv-asset": {
        maxWidth: "45vw"
    },
    "& .vv-visual": {
        overflow: "visible"
    },
    maxWidthTablet: {
        "& .vv-asset": {
            maxWidth: "100%"
        },
        "& .vv-video": {
            visibility: "inherit",
            maxWidth: "100%"
        }
    },
};

const MarqueeEyebrow = createComponent(() => textStyles.marqueeEyebrow, "h4",["dangerouslySetInnerHTML"]);

const eyebrowStyles = {
    display: "flex",
    alignItems: "center",
    maxWidthTablet: {
        justifyContent: "center"
    },

    "& img": {
        marginRight: "12px"
    }
};

const BaseMarqueeHeading = createComponent(
    ({ headingSize = "regular" }) => ({
        maxWidthTabletLandscape: {
            "& br": { display: "none" }
        },

        // Support multiple H1 heading sizes
        extend: [
            {
                condition: headingSize === "large",
                style: {
                    ...textStyles.heading1XL,
                }
            },
            {
                condition: headingSize === "regular",
                style: {
                    ...textStyles.heading1,
                }
            },
            {
                condition: headingSize === "medium",
                style: {
                    ...textStyles.heading1Medium,
                }
            },
            {
                condition: headingSize === "small",
                style: {
                    ...textStyles.heading1Small,
                }
            },
            {
                condition: headingSize === "extrasmall",
                style: {
                    ...textStyles.heading1XS,
                }
            }
        ]
    }),
    "h1",
    ["as", "dangerouslySetInnerHTML"]
);

const MarqueeCopy = createComponent(() => textStyles.paragraphLarge, "p", [
    "dangerouslySetInnerHTML"
]);

const MarqueeContainer = createComponent(() => textStyles.paragraphLarge, "div", [
    "dangerouslySetInnerHTML"
]);

const marqueeHeadingStyles = {
    extend: [fluid("marginBottom", { max: 35, min: 20 })]
};

const marqueeCopyStyles = layout => ({
    width: 600,
    maxWidth: "100%",

    extend: [
        {
            condition: layout === "centered",
            style: {
                marginH: "auto"
            }
        }
    ]
});

const videoPlayerStyles = {
    width: "100%"
};

const formStyles = {
    width: "100%",
    background: colors.white,
    padding: 40,
    borderColor: colors.blue,
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: 12,
    "& h3": {
        display: "none"
    },
    "& .hs_firstname": {
        width: "calc(50% - 9px)",
        display: "inline-block",
        marginRight: 9
    },
    "& .hs_lastname": {
        width: "calc(50% - 9px)",
        display: "inline-block",
        marginLeft: 9
    },
    "& .hs-form-field .hs-error-msg": {
        display: "none"
    },
    "& .hs-submit": {
        marginTop: 20,
        "& input": {
            lineHeight: "50px"
        }
    }
};
